import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  actionKey, actionToSan, applyAction, applyAdmin, createGame, legalActions, opp, sqName, viewFor,
  type Action, type AdminAction, type Color, type GameEvent, type GameResult, type GameState, type MoveRecord, type MoveReview, type Ruleset, type TimeControl,
} from '@ic/engine';
import { idb, type SavedGame } from './idb';
import { requestReview } from './review';
import { sfx } from './sound';

export type LocalMode = 'local' | 'bot' | 'sandbox';

interface Entry { state: GameState; action: Action | null; events: GameEvent[]; move: MoveRecord | null }

let botWorker: Worker | null = null;
let botReq = 0;
function bot(): Worker {
  botWorker ??= new Worker(new URL('../workers/bot.worker.ts', import.meta.url), { type: 'module' });
  return botWorker;
}

export interface LocalGameOptions {
  ruleset: Ruleset;
  mode: LocalMode;
  botLevel: number;
  humanColor: Color;
  tc: TimeControl | null;
  names?: Record<Color, string>;
}

/** Offline game controller: hot-seat, vs. bot (Web Worker) or rule-test sandbox. */
export function useLocalGame(o: LocalGameOptions) {
  const initial = useMemo(() => createGame(o.ruleset), [o.ruleset]);
  const [entries, setEntries] = useState<Entry[]>(() => [{ state: initial, action: null, events: [], move: null }]);
  const [extra, setExtra] = useState<GameResult | null>(null);
  const [eventsKey, setEventsKey] = useState(0);
  const timed = !!o.tc && o.tc.initial > 0 && o.mode !== 'sandbox';
  const [clocks, setClocks] = useState({ w: (o.tc?.initial ?? 0) * 1000, b: (o.tc?.initial ?? 0) * 1000 });
  const turnStart = useRef(Date.now());
  const [now, setNow] = useState(Date.now());
  const [thinking, setThinking] = useState(false);
  // Move ratings by move number (1 = first move); cleared on restart.
  const [reviews, setReviews] = useState<Record<number, MoveReview>>({});
  const [pondering, setPondering] = useState<{ action: Action; depth: number; final: boolean } | null>(null);
  const gameId = useRef(crypto.randomUUID());
  const saved = useRef(false);

  const cur = entries[entries.length - 1];
  const result = cur.state.result ?? extra;

  const reset = useCallback(() => {
    setEntries([{ state: initial, action: null, events: [], move: null }]);
    setExtra(null);
    setClocks({ w: (o.tc?.initial ?? 0) * 1000, b: (o.tc?.initial ?? 0) * 1000 });
    turnStart.current = Date.now();
    gameId.current = crypto.randomUUID();
    saved.current = false;
    setReviews({});
  }, [initial, o.tc]);

  useEffect(() => { reset(); }, [reset]);

  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const extraRef = useRef(extra);
  extraRef.current = extra;

  const play = useCallback((a: Action) => {
    const es = entriesRef.current;
    const last = es[es.length - 1];
    if (last.state.result || extraRef.current) return;
    const viewer = o.mode === 'bot' ? o.humanColor : o.mode === 'local' ? last.state.turn : null;
    const r = applyAction(last.state, a);
    const san = actionToSan(last.state, a, r.state, viewer);
    if (r.state.turn !== last.state.turn && timed) {
      const mover = last.state.turn;
      const t = Date.now();
      const spent = t - turnStart.current;
      setClocks((c) => ({ ...c, [mover]: c[mover] - spent + (o.tc?.increment ?? 0) * 1000 }));
      turnStart.current = t;
    }
    const next = [...es, { state: r.state, action: a, events: r.events, move: { san, color: last.state.turn } }];
    entriesRef.current = next;
    setEntries(next);
    setEventsKey((k) => k + 1);
    const n = next.length - 1;
    const game = gameId.current;
    void requestReview(last.state, a).then((r) => {
      if (r && game === gameId.current && entriesRef.current[n]?.action === a) setReviews((m) => ({ ...m, [n]: r }));
    });
  }, [o.mode, o.humanColor, o.tc, timed]);

  // Clock ticking + flag fall.
  useEffect(() => {
    if (!timed || result) return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      const turn = cur.state.turn;
      if (clocks[turn] - (t - turnStart.current) <= 0) setExtra({ winner: opp(turn), reason: 'timeout' });
    }, 100);
    return () => clearInterval(id);
  }, [timed, result, cur.state.turn, clocks]);

  // Bot move: search in the worker, then "think out loud" by showing the candidates it weighed.
  useEffect(() => {
    if (o.mode !== 'bot' || result || cur.state.turn === o.humanColor) return;
    const id = ++botReq;
    const started = Date.now();
    const timers: ReturnType<typeof setTimeout>[] = [];
    setThinking(true);
    const w = bot();
    const onMsg = (e: MessageEvent<{ id: number; action: Action | null; candidates: Action[]; depth: number }>) => {
      if (e.data.id !== id) return;
      w.removeEventListener('message', onMsg);
      const best = e.data.action;
      if (!best) { setThinking(false); return; }
      // Stronger bots consider more moves and take a little longer.
      const level = o.botLevel;
      const others = e.data.candidates
        .filter((a) => actionKey(a) !== actionKey(best))
        .slice(0, Math.min(3, 1 + Math.floor(level / 2)))
        .sort(() => Math.random() - 0.5);
      const sequence = [...others, best];
      const budget = 700 + level * 280 + Math.random() * 500;
      const remaining = Math.max(0, budget - (Date.now() - started));
      const step = Math.max(320, remaining / sequence.length);
      sequence.forEach((a, i) => timers.push(setTimeout(
        () => setPondering({ action: a, depth: e.data.depth, final: i === sequence.length - 1 }), i * step)));
      timers.push(setTimeout(() => { setPondering(null); setThinking(false); play(best); }, sequence.length * step + 180));
    };
    w.addEventListener('message', onMsg);
    w.postMessage({ id, state: cur.state, level: o.botLevel });
    return () => {
      w.removeEventListener('message', onMsg);
      timers.forEach(clearTimeout);
      setThinking(false);
      setPondering(null);
    };
  }, [cur, o.mode, o.humanColor, o.botLevel, result, play]);

  // Save finished games for history + later sync.
  const save = (result: GameResult, quiet = false) => {
    if (saved.current || o.mode === 'sandbox' || entries.length < 2) return;
    saved.current = true;
    if (!quiet) sfx.end();
    const g: SavedGame = {
      id: gameId.current, mode: o.mode, rulesetName: o.ruleset.name, ruleset: o.ruleset,
      white: o.names?.w ?? 'White', black: o.names?.b ?? 'Black',
      result: result.winner === 'w' ? '1-0' : result.winner === 'b' ? '0-1' : '½-½', reason: result.reason,
      moves: entries.flatMap((e) => (e.move ? [e.move.san] : [])), playedAt: Date.now(), synced: false,
    };
    void idb.put('games', g);
  };
  useEffect(() => { if (result) save(result); });

  const undo = useCallback(() => {
    setExtra(null);
    setEntries((es) => {
      if (es.length <= 1) return es;
      let n = es.slice(0, -1);
      if (o.mode === 'bot') while (n.length > 1 && n[n.length - 1].state.turn !== o.humanColor) n = n.slice(0, -1);
      return n;
    });
    turnStart.current = Date.now();
    setEventsKey((k) => k + 1);
  }, [o.mode, o.humanColor]);

  const viewer: Color | null = o.mode === 'sandbox' ? null : o.mode === 'bot' ? o.humanColor : cur.state.turn;
  const display = useMemo(() => {
    const shown = viewer ? viewFor(cur.state, viewer) : cur.state;
    return result && !cur.state.result ? { ...shown, result } : shown;
  }, [cur, viewer, result]);
  const humanTurn = o.mode !== 'bot' || cur.state.turn === o.humanColor;
  const legal = useMemo(() => (result || !humanTurn ? [] : legalActions(cur.state)), [cur, result, humanTurn]);
  const elapsed = timed && !result ? now - turnStart.current : 0;
  const clockFor = (c: Color) => (timed ? Math.max(0, clocks[c] - (cur.state.turn === c ? Math.max(0, elapsed) : 0)) : null);

  // Earlier positions for move review, seen by the same player as the live board.
  const positions = useMemo(() => entries.map((e) => {
    const v = o.mode === 'sandbox' ? null : o.mode === 'bot' ? o.humanColor : e.state.turn;
    return { state: v ? viewFor(e.state, v) : e.state, lastAction: e.action };
  }), [entries, o.mode, o.humanColor]);

  return {
    initial, state: display, legal, result, entries, eventsKey, thinking, positions, pondering, reviews,
    ponderSan: pondering ? actionToSan(cur.state, pondering.action) : null,
    moves: entries.flatMap((e) => (e.move ? [e.move] : [])),
    lastAction: cur.action, events: cur.events,
    clock: clockFor,
    play, undo, reset,
    resign: (c: Color) => setExtra({ winner: opp(c), reason: 'resignation' }),
    /** Admin tools: nuke, spawn or win for `c`. Not a move: the turn does not change. */
    admin: (c: Color, a: AdminAction) => {
      const es = entriesRef.current;
      const last = es[es.length - 1];
      if (last.state.result || extraRef.current) return;
      const r = applyAdmin(last.state, c, a);
      const def = a.kind === 'spawn' ? last.state.rs.pieces[a.type] : null;
      const san = a.kind === 'nuke' ? 'NUKE' : a.kind === 'win' ? 'admin win' : `+${def?.letter || def?.name[0] || '?'}${sqName(a.sq, last.state.w)}`;
      const next = [...es, { state: r.state, action: last.action, events: r.events, move: { san, color: c } }];
      entriesRef.current = next;
      setEntries(next);
      setEventsKey((k) => k + 1);
    },
    /** Give up while leaving the page: saved right away, since the page is about to unmount. */
    forfeit: (c: Color) => { const r = { winner: opp(c), reason: 'resignation' }; save(r, true); setExtra(r); },
    draw: () => setExtra({ winner: null, reason: 'agreement' }),
  };
}
